import logService from '@/services/logService';
import { LOG_CATEGORIES } from '@/services/logService';
import axios from '@/api/axios';

const state = {
  logsEnabled: true,
  pendingLogsCount: 0,
  recentLogs: [],
  syncStatus: {
    lastSync: null,
    inProgress: false,
    autoSyncEnabled: true
  },
  adminDashboardStats: null
};

const getters = {
  pendingLogsCount: state => state.pendingLogsCount,
  recentLogs: state => state.recentLogs,
  syncStatus: state => state.syncStatus,
  logsEnabled: state => state.logsEnabled,
  adminDashboardStats: state => state.adminDashboardStats
};

const actions = {
  /**
   * Initialiser le service de logs
   */
  initLogService({ commit, dispatch }, config = {}) {
    // Initialiser avec la configuration combinée
    logService.init({
      // Permettre de désactiver les logs à partir du localStorage
      enabled: localStorage.getItem('logs_enabled') !== 'false',
      // Ajouter d'autres configurations spécifiques ici
      ...config
    });
    
    // Mise à jour initiale des compteurs
    dispatch('updatePendingLogsCount');
    
    // Programmer des mises à jour périodiques
    setInterval(() => {
      dispatch('updatePendingLogsCount');
      dispatch('updateRecentLogs');
    }, 60000); // Rafraîchir chaque minute
    
    commit('SET_LOGS_ENABLED', logService.config.enabled);
    
    return logService;
  },
  
  /**
   * Méthodes de logging à différents niveaux
   */
  debug({ state }, { message, data = {}, category = LOG_CATEGORIES.DEFAULT, sendImmediately = true }) {
    if (!state.logsEnabled) return Promise.resolve();
    return logService.debug(message, data, category, sendImmediately);
  },
  
  info({ state }, { message, data = {}, category = LOG_CATEGORIES.DEFAULT, sendImmediately = true }) {
    if (!state.logsEnabled) return Promise.resolve();
    return logService.info(message, data, category, sendImmediately);
  },
  
  warn({ state }, { message, data = {}, category = LOG_CATEGORIES.DEFAULT, sendImmediately = true }) {
    if (!state.logsEnabled) return Promise.resolve();
    return logService.warn(message, data, category, sendImmediately);
  },
  
  error({ state }, { message, data = {}, category = LOG_CATEGORIES.DEFAULT, sendImmediately = true }) {
    if (!state.logsEnabled) return Promise.resolve();
    return logService.error(message, data, category, sendImmediately);
  },
  
  critical({ state }, { message, data = {}, category = LOG_CATEGORIES.DEFAULT, sendImmediately = true }) {
    if (!state.logsEnabled) return Promise.resolve();
    return logService.critical(message, data, category, sendImmediately);
  },
  
  /**
   * Méthodes spécifiques pour les logs de synchronisation
   */
  syncDebug({ state }, { message, data = {}, sendImmediately = true }) {
    if (!state.logsEnabled) return Promise.resolve();
    return logService.syncDebug(message, data, sendImmediately);
  },
  
  syncInfo({ state }, { message, data = {}, sendImmediately = true }) {
    if (!state.logsEnabled) return Promise.resolve();
    return logService.syncInfo(message, data, sendImmediately);
  },
  
  syncWarning({ state }, { message, data = {}, sendImmediately = true }) {
    if (!state.logsEnabled) return Promise.resolve();
    return logService.syncWarning(message, data, sendImmediately);
  },
  
  syncError({ state }, { message, data = {}, sendImmediately = true }) {
    if (!state.logsEnabled) return Promise.resolve();
    return logService.syncError(message, data, sendImmediately);
  },
  
  /**
   * Capture et log des erreurs pour une fonction
   */
  async capture({ state }, { fn, context = 'Unknown operation', category = LOG_CATEGORIES.DEFAULT }) {
    if (!state.logsEnabled) return fn();
    
    try {
      return await fn();
    } catch (error) {
      this.error({
        message: `Error in ${context}`,
        data: {
          error: {
            message: error.message,
            stack: error.stack,
            name: error.name
          }
        },
        category
      });
      throw error;
    }
  },
  
  /**
   * Mettre à jour le nombre de logs en attente
   */
  async updatePendingLogsCount({ commit }) {
    try {
      const logs = await logService.exportLogs();
      const pendingLogs = logs.filter(log => !log.synced).length;
      commit('SET_PENDING_LOGS_COUNT', pendingLogs);
      return pendingLogs;
    } catch (error) {
      console.error('Failed to update pending logs count:', error);
      return state.pendingLogsCount;
    }
  },
  
  /**
   * Mettre à jour la liste des logs récents
   */
  updateRecentLogs({ commit }) {
    // Récupérer les logs du buffer mémoire du service
    const recentLogs = logService.memoryBuffer.slice(-10);
    commit('SET_RECENT_LOGS', recentLogs);
    return recentLogs;
  },
  
  /**
   * Forcer une synchronisation des logs
   */
  async synchronizeLogs({ commit, dispatch }) {
    try {
      commit('SET_SYNC_IN_PROGRESS', true);
      await logService.syncLogs();
      commit('SET_LAST_SYNC', new Date().toISOString());
      await dispatch('updatePendingLogsCount');
      return true;
    } catch (error) {
      console.error('Failed to synchronize logs:', error);
      return false;
    } finally {
      commit('SET_SYNC_IN_PROGRESS', false);
    }
  },
  
  /**
   * Activer/désactiver les logs
   */
  setLogsEnabled({ commit }, enabled) {
    localStorage.setItem('logs_enabled', enabled.toString());
    commit('SET_LOGS_ENABLED', enabled);
    
    // Si on active les logs, réinitialiser le service
    if (enabled && !logService.initialized) {
      logService.init({
        enabled
      });
    }
  },
  
  /**
   * Activer/désactiver la synchronisation automatique
   */
  setAutoSyncEnabled({ commit }, enabled) {
    if (enabled) {
      logService.startAutoSync();
    } else {
      logService.stopAutoSync();
    }
    
    commit('SET_AUTO_SYNC_ENABLED', enabled);
  },
  
  /**
   * Exporter tous les logs au format JSON
   */
  async exportLogs() {
    try {
      const logs = await logService.exportLogs();
      // Créer un fichier téléchargeable
      const dataStr = JSON.stringify(logs, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      
      const exportFileDefaultName = `logs_export_${new Date().toISOString()}.json`;
      
      // Créer un lien de téléchargement et le déclencher
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
      
      return true;
    } catch (error) {
      console.error('Failed to export logs:', error);
      return false;
    }
  },
  
  /**
   * Purger tous les logs
   */
  async clearLogs({ dispatch }) {
    try {
      await logService.clearLogs();
      await dispatch('updatePendingLogsCount');
      await dispatch('updateRecentLogs');
      return true;
    } catch (error) {
      console.error('Failed to clear logs:', error);
      return false;
    }
  },
  
  /**
   * Charger les statistiques pour le tableau de bord d'administration
   */
  async loadAdminDashboardStats({ commit }) {
    try {
      // Requête à l'API pour obtenir les statistiques
      const response = await axios.get('/livraison/logs/appDelivery/stats/');
      commit('SET_ADMIN_DASHBOARD_STATS', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to load admin dashboard stats:', error);
      return null;
    }
  }
};

const mutations = {
  SET_PENDING_LOGS_COUNT(state, count) {
    state.pendingLogsCount = count;
  },
  SET_RECENT_LOGS(state, logs) {
    state.recentLogs = logs;
  },
  SET_SYNC_IN_PROGRESS(state, inProgress) {
    state.syncStatus.inProgress = inProgress;
  },
  SET_LAST_SYNC(state, timestamp) {
    state.syncStatus.lastSync = timestamp;
  },
  SET_AUTO_SYNC_ENABLED(state, enabled) {
    state.syncStatus.autoSyncEnabled = enabled;
  },
  SET_LOGS_ENABLED(state, enabled) {
    state.logsEnabled = enabled;
  },
  SET_ADMIN_DASHBOARD_STATS(state, stats) {
    state.adminDashboardStats = stats;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}; 